import { useState, useMemo, CSSProperties, useCallback } from 'react'

import { KojinResult } from "interface/kojin"

import { isValid, SisetuDoctorsCondition, SisetuDoctorsConditions } from './condition'

import {
  Box, BoxProps, Stack, Alert, Typography, Card, Chip,
  List, ListItem, ListItemButton, 
  FormControlLabel, Checkbox, 
  Accordion as MuiAccordion, AccordionProps, 
  AccordionSummary as MuiAccordionSummary, AccordionSummaryProps, 
  AccordionDetails as MuiAccordionDetails, AccordionDetailsProps,
  styled,
  useTheme,
  useMediaQuery,
  ChipProps
} from "@mui/material"

import { ExpandIcon } from 'shared/icons'

import { Masonry } from '@mui/lab'
import { numberFormat } from 'shared/util'
import { useSearch } from 'features/search/context'

const selectedCardStyle: CSSProperties = {
  backgroundColor: 'rgba(25, 118, 210, 0.08)'
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  background: 'none',
  paddingX: theme.spacing(2),
  paddingY: 0,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  }
}))

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ExpandIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(() => ({
  flexDirection: 'row-reverse',
  padding: '0 14px',
  margin: 0,
  minHeight: 'auto',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    padding: 0,
    margin: 0
  },
}))

const AccordionDetails = styled((props:AccordionDetailsProps) => (
  <MuiAccordionDetails
    {...props}
  />
))(() => ({
  padding: 0
}))

interface OrganRecord {
  bukaCode: string | null,
  bukaHojoCode: string | null,
  bukaName: string | null,
  bukaNameKana: string | null,
  yakusyokuCode: string
  yakusyokuName: string
  positionRank: number
  kojinCode: string
  kojinName: string
  kojinNameKana: string
  score: KojinResult["score"]
  guideLine: KojinResult["guideLine"]
}

interface OrganData {
  yakusyokuList: {
    yakusyokuCode: string
    yakusyokuName: string
    count: number
  }[]
  tree: {
    bukaCode: string
    bukaName: string
    list: OrganRecord[]
    withOut: OrganRecord[]
    resident: OrganRecord[]
  }[]
}

const defaultChipsProps: ChipProps = {
  size: 'small',
  sx: {
    height: "1.2rem",
    fontSize: "x-small",
    borderRadius: 4
  }
}

type GuideLineScoreChipProps = {
  kojinCode: string
  guideLines: KojinResult["guideLine"]
} & ChipProps
const GuideLineScoreChip = ({
  kojinCode,
  guideLines,
  ...others
}: GuideLineScoreChipProps) => {
  const search = useSearch()
  const guideLineTypes = useMemo(
    () => {
      if (!search || !search.guideLine || !guideLines || guideLines.length === 0) return []

      const master = search.guideLine
			const guideLineTypes: string[] = []
			for(const { guideLineId } of guideLines) {
				const guideLineMaster = master.find(m => m.guideLineId === guideLineId)
				if (!guideLineMaster) continue

				const guideLineType = guideLineMaster.guideLineType ?? 'ガイドライン'
        if (!guideLineTypes.includes(guideLineType)) {
          guideLineTypes.push(guideLineType)
        }
			}
			return guideLineTypes
    },
    [search, guideLines]
  )
  return guideLineTypes.map((guideLineType, i) => (
    <Chip
      key={`glt-${kojinCode}-${i}`}
      {...defaultChipsProps}
      color="senmoni"
      label={guideLineType}
      {...others}
    />
  ))
}

type JournalScoreChipProps = {
  count: number | undefined | null
} & ChipProps
const JournalScoreChip = ({
  count,
  ...others
}: JournalScoreChipProps) => {
  return count ? (
    <Chip
      {...defaultChipsProps}
      color="journal"
      label={`論文 ${numberFormat(count)}件`}
      {...others}
    />
  ) : <></>
}

type TrialScoreChipProps = {
  count: number | undefined | null
} & ChipProps
const TrialScoreChip = ({
  count,
  ...others
}: TrialScoreChipProps) => {
  return count ? (
    <Chip
      {...defaultChipsProps}
      color="trial"
      label={`治験 ${numberFormat(count)}件`}
      {...others}
    />
  ) : <></>
}

type RazyAccordionProps = {
  title: string
  list: OrganRecord[]
  onKojinOpen: (kojinCode: string) => void
}
const RazyAccordion = ({
  title,
  list,
  onKojinOpen
}: RazyAccordionProps) => {
  const [expanded, setExpanded] = useState<boolean>(false)
  return (
    <Accordion
      expanded={expanded}
      disableGutters
      sx={{ padding: 0, boxShadow: 'none' }}
      onChange={(e, exp) => setExpanded(exp)}
    >
      <AccordionSummary>
        <Stack direction='row' paddingX={1} paddingY={0.5} spacing={2} alignItems='center'>
          <Typography variant='body2' color='text.secondary'>{title}</Typography>
          <Typography variant='body1'>{list.length} 人</Typography>
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <List disablePadding>
          {expanded && list.map((record, j) => (
            <ListItem key={`expanded-record-${j}`} disablePadding>
              <ListItemButton sx={{ paddingX: 2, paddingY: 0.25 }} onClick={() => onKojinOpen(record.kojinCode)}>
                <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' width="100%">
                  {(record.yakusyokuName && record.yakusyokuName !== title) && (
                    <Typography variant='body2' color='text.secondary'>{record.yakusyokuName}</Typography>
                  )}                    
                  <Typography variant='body1'>{record.kojinName}</Typography>
                  <Stack flexGrow={1} direction='row' spacing={0.5} justifyContent="flex-end" alignItems='center' flexWrap="wrap">
                    <GuideLineScoreChip kojinCode={record.kojinCode} guideLines={record.guideLine}/>
                    <JournalScoreChip count={record.score?.journal?.counts?.total}/>
                    <TrialScoreChip count={record.score?.trial?.trialCount}/>
                  </Stack>
                </Stack>
              </ListItemButton>
            </ListItem>
          )) }
        </List>
      </AccordionDetails>
    </Accordion>
  )
}

const isWithOut = (yakusyokuCode: string) => ['', '501'].indexOf(yakusyokuCode) >= 0
const isResident = (yakusyokuCode: string) => ['604', '605', '607', '608', '609', '701'].indexOf(yakusyokuCode) >= 0

type SisetuPreviewDoctorsOrganProps = {
  sisetuCode: string
  kojinList?: KojinResult[]
  condition: SisetuDoctorsConditions
  onChangeCondition: (axisKey: 'buka', condition: SisetuDoctorsCondition) => void
  onKojinOpen: (kojinCode: string) => void
} & BoxProps
export const SisetuPreviewDoctorsOrgan = ({
  sisetuCode,
  kojinList,
  condition,
  onChangeCondition,
  onKojinOpen,
  ...others
}: SisetuPreviewDoctorsOrganProps) => {
  const theme = useTheme()
  const isSmallMedia = useMediaQuery(theme.breakpoints.down('sm'))
  const [ignoreYakusyokuList, setIgnoreYakusyokuList] = useState<string[]>([])

  const organData = useMemo<OrganData | null>(
    () => {
      if (!kojinList) return null

      const organList: OrganRecord[] = []
      for(const kojin of kojinList) {
        if (!kojin.kojinCode || !kojin.organ || !isValid(sisetuCode, condition, kojin, 'buka')) continue

        for(const organ of kojin.organ) {
          if (organ.sisetuCode !== sisetuCode) continue

          organList.push({
            bukaCode: organ.bukaCode ?? null,
            bukaHojoCode: null,
            bukaName: organ.bukaName ?? null,
            bukaNameKana: null,
            yakusyokuCode: organ.yakusyokuCode ?? '',
            yakusyokuName: organ.yakusyokuName ?? '',
            positionRank: organ.positionRank ?? 0,
            kojinCode: kojin.kojinCode,
            kojinName: kojin.isiName ?? '',
            kojinNameKana: kojin.isiNameKana ?? '',
            score: kojin.score,
            guideLine: kojin.guideLine
          })
        }
      }
      organList.sort((a, b) =>
        ((a.positionRank ?? 10000) * 1000 + parseInt(a.yakusyokuCode ?? '0')) -
        ((b.positionRank ?? 10000) * 1000 + parseInt(b.yakusyokuCode ?? '0')))
      const data: OrganData = {
        yakusyokuList: [],
        tree: []
      }
      for(const organ of organList) {
        const { code, name } = isWithOut(organ.yakusyokuCode) ? {
          code: 'withOut',
          name: '役職なし'
        } : isResident(organ.yakusyokuCode) ? {
          code: 'resident',
          name: '研修医'
        } : {
          code: organ.yakusyokuCode,
          name: organ.yakusyokuName
        }
        if (ignoreYakusyokuList.indexOf(code) < 0) {
          let itb = data.tree.find((t) => t.bukaCode === (organ.bukaCode ?? ''))
          if (!itb) {
            itb = {
              bukaCode: organ.bukaCode ?? '',
              bukaName: organ.bukaName ?? '',
              list: [],
              withOut: [],
              resident: []
            }
            data.tree.push(itb)
          }
          if (code === 'withOut') itb.withOut.push(organ)
          else if (code === 'resident') itb.resident.push(organ)
          else itb.list.push(organ)
        }
        let ity = data.yakusyokuList.find((t) => t.yakusyokuCode === code)
        if (!ity) {
          ity = {
            yakusyokuCode: code,
            yakusyokuName: name,
            count: 0
          }
          data.yakusyokuList.push(ity)
        }
        ity.count++
      }
      return data
    },
    [sisetuCode, kojinList, condition, ignoreYakusyokuList]
  )

  const onChangeYakusyokuChecks = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const value = ev.target.name
      const currentIndex = ignoreYakusyokuList.indexOf(value)
      const newChecked = [...ignoreYakusyokuList]
      if (!ev.target.checked) {
        if (currentIndex < 0) newChecked.push(value)
      } else {
        if (currentIndex >= 0) newChecked.splice(currentIndex, 1)
      }
      setIgnoreYakusyokuList(newChecked)
    },
    [ignoreYakusyokuList, setIgnoreYakusyokuList]
  )

  const onClickedBuka = useCallback(
    (bukaCode: string, bukaName: string) => {
      onChangeCondition('buka', { code: bukaCode, name: bukaName })
    },
    [onChangeCondition]
  )

  return (
    <Box {...others}>
      <Stack
        direction='column'
        height='100%'
        position='relative'
        overflow={isSmallMedia ? 'auto' : 'hidden'}
      >
        { organData?.yakusyokuList ? 
          <Stack
            direction='row'
            alignItems='flex-start'
            flexWrap='wrap'
            useFlexGap
            paddingY={1} paddingX={2}
            spacing={0}
            sx={{
              borderBottom: 1,
              borderColor: 'divider'
            }}
          >
            {organData.yakusyokuList.map((yakusyoku, i) => (
              <FormControlLabel
                key={`yakusyoku-${i}`}
                label={`${yakusyoku.yakusyokuName} (${yakusyoku.count})`}
                sx={{ whiteSpace: 'nowrap' }}
                control={<Checkbox
                  checked={ignoreYakusyokuList.indexOf(yakusyoku.yakusyokuCode) < 0 ? true : false}
                  color="primary"
                  name={yakusyoku.yakusyokuCode}
                  size="small"
                  onChange={onChangeYakusyokuChecks}
                />}
              />
            ))}
          </Stack> : <></> }

        {(organData && organData.tree.length === 0) ? (
          <Box padding={1}>
            <Alert severity="info">対象が見つかりません.</Alert>
          </Box>
        ) : (
          <Box
            flexGrow={1}
            overflow={isSmallMedia ? undefined : 'auto'}
            padding={isSmallMedia ? 0.5 : 1}
            sx={{
              background: 'GhostWhite'
            }}
          >
            <Masonry
              columns={{ xs: 1, sm: 2, md: 3, lg: 4 }}
              spacing={isSmallMedia ? 0.5 : 1}
            >
              {organData?.tree.map((organ, i) => (
                <Card
                  key={`organ-${i}`}
                  square={isSmallMedia}
                  style={organ.bukaCode === condition.buka?.code ? selectedCardStyle : undefined }
                >
                  { (organ.bukaName !== '') && (
                    <Typography
                      variant='body1'
                      component='div'
                      padding={1}
                      fontWeight='bold'
                      onClick={() => onClickedBuka(organ.bukaCode, organ.bukaName)}
                      sx={{
                        cursor: 'pointer',
                        borderBottom: 1,
                        borderColor: 'divider',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      {organ.bukaName}
                    </Typography>
                  ) }
                  <Box paddingY={1}>
                    <List disablePadding>
                      { organ.list.map((record, j) => (
                        <ListItem key={`organ-${i}-record-${j}`} disablePadding>
                          <ListItemButton sx={{ paddingX: 1, paddingY: 0.25 }} onClick={() => onKojinOpen(record.kojinCode)}>
                            <Stack key={`organ-${i}-record-${j}`} direction='row' spacing={1} alignItems='center' flexWrap="wrap" width='100%'>
                              <Typography variant='body2' color='text.secondary'>{record.yakusyokuName}</Typography>
                              <Typography variant='body1'>{record.kojinName}</Typography>
                              <Stack flexGrow={1} direction='row' spacing={0.5} justifyContent="flex-end" alignItems='center'>
                                <GuideLineScoreChip kojinCode={record.kojinCode} guideLines={record.guideLine}/>
                                <JournalScoreChip count={record.score?.journal?.counts?.total}/>
                                <TrialScoreChip count={record.score?.trial?.trialCount}/>
                              </Stack>
                            </Stack>
                          </ListItemButton>
                        </ListItem>
                      )) }
                    </List>
                    
                    { organ.withOut.length > 0 ? (
                      <RazyAccordion 
                        title='役職なし'
                        list={organ.withOut}
                        onKojinOpen={onKojinOpen}
                      />
                    ) : <></> }

                    { organ.resident.length > 0 ? (
                      <RazyAccordion
                        title='研修医'
                        list={organ.resident}
                        onKojinOpen={onKojinOpen}
                      />
                    ) : <></> }
                  </Box>
                </Card>
              )) || <></> }
            </Masonry>
          </Box>
        )}
      </Stack>
    </Box>
  )
}